import React, { useMemo, useCallback } from 'react';
import BodyMapFigure from './BodyMapFigure';
import SelectedRegionsPanel from './SelectedRegionsPanel';
import { VIEW_ANTERIOR, VIEW_POSTERIOR } from './constants';

// Single source of truth for the Selected Areas panel's default placement —
// anchored to the right of the figures. Both the builder canvas and the survey/
// preview renderer rely on this so they lay the panel out identically. When the
// element is too narrow to fit it here, SelectedRegionsPanel falls back to inline
// (stacked below the figures) — see the containerWidth check there.
const DEFAULT_PANEL_POSITION = { x: 470, y: 0 };

export default function BodyMap({
  value = [],
  onChange,
  disabled = false,
  editMode = false,
  overrides,
  activeSlug,
  onActiveSlugChange,
  onOverrideChange,
  panelPosition,
  onPanelPositionChange,
  containerWidth,
}) {
  const selectedSlugs = useMemo(() => new Set(value), [value]);
  const effectivePanelPosition = panelPosition ?? DEFAULT_PANEL_POSITION;

  const handleToggle = useCallback(
    (slug) => {
      if (selectedSlugs.has(slug)) {
        onChange?.(value.filter((s) => s !== slug));
      } else {
        onChange?.([...value, slug]);
      }
    },
    [selectedSlugs, value, onChange],
  );

  const handleRemove = useCallback(
    (slug) => onChange?.(value.filter((s) => s !== slug)),
    [value, onChange],
  );

  return (
    <div className="flex gap-6 items-start flex-wrap relative">
      <div className="flex gap-4 bg-gray-50 border rounded-lg p-4">
        <BodyMapFigure
          view={VIEW_ANTERIOR}
          title="Front"
          selectedSlugs={selectedSlugs}
          onToggle={handleToggle}
          disabled={disabled}
          editMode={editMode}
          overrides={overrides}
          activeSlug={activeSlug}
          onActiveSlugChange={onActiveSlugChange}
          onOverrideChange={onOverrideChange}
        />
        <BodyMapFigure
          view={VIEW_POSTERIOR}
          title="Back"
          selectedSlugs={selectedSlugs}
          onToggle={handleToggle}
          disabled={disabled}
          editMode={editMode}
          overrides={overrides}
          activeSlug={activeSlug}
          onActiveSlugChange={onActiveSlugChange}
          onOverrideChange={onOverrideChange}
        />
      </div>

      <SelectedRegionsPanel
        selectedSlugs={selectedSlugs}
        onRemove={handleRemove}
        disabled={disabled || editMode}
        editMode={editMode}
        panelPosition={effectivePanelPosition}
        onPanelPositionChange={onPanelPositionChange}
        containerWidth={containerWidth}
      />
    </div>
  );
}
